import React from 'react';

interface EmailDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailNotification: any;
}

const EmailDetailModal: React.FC<EmailDetailModalProps> = ({
  isOpen,
  onClose,
  emailNotification
}) => {
  if (!isOpen || !emailNotification) return null;

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'SENT':
        return { text: 'Đã gửi', class: 'bg-green-100 text-green-800' };
      case 'PENDING':
        return { text: 'Đang chờ', class: 'bg-yellow-100 text-yellow-800' };
      case 'FAILED':
        return { text: 'Thất bại', class: 'bg-red-100 text-red-800' };
      case 'CANCELLED':
        return { text: 'Đã hủy', class: 'bg-gray-100 text-gray-800' };
      default:
        return { text: status, class: 'bg-gray-100 text-gray-800' };
    }
  };

  const statusInfo = getStatusInfo(emailNotification.status);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Chi tiết email thông báo phí</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #ccc; padding-bottom: 10px; margin-bottom: 20px; }
              .info-row { margin: 10px 0; }
              .label { font-weight: bold; }
              .content { margin-top: 20px; padding: 15px; border: 1px solid #ddd; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Chi tiết email thông báo phí</h2>
            </div>
            <div class="info-row">
              <span class="label">Tiêu đề:</span> ${emailNotification.subject}
            </div>
            <div class="info-row">
              <span class="label">Email nhận:</span> ${emailNotification.recipientEmail}
            </div>
            <div class="info-row">
              <span class="label">Trạng thái:</span> ${statusInfo.text}
            </div>
            <div class="info-row">
              <span class="label">Ngày tạo:</span> ${new Date(emailNotification.createdAt).toLocaleString('vi-VN')}
            </div>
            ${emailNotification.sentAt ? `
              <div class="info-row">
                <span class="label">Ngày gửi:</span> ${new Date(emailNotification.sentAt).toLocaleString('vi-VN')}
              </div>
            ` : ''}
            ${emailNotification.failureReason ? `
              <div class="info-row">
                <span class="label">Lý do thất bại:</span> ${emailNotification.failureReason}
              </div>
            ` : ''}
            <div class="content">
              <h3>Nội dung email:</h3>
              ${emailNotification.content}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Chi tiết thông báo phí</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              In
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6">
            {/* Email Information */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Thông tin email</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề</label>
                  <p className="text-sm text-gray-900 bg-white p-2 rounded border">{emailNotification.subject}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email nhận</label>
                  <p className="text-sm text-gray-900 bg-white p-2 rounded border">{emailNotification.recipientEmail}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${statusInfo.class}`}>
                    {statusInfo.text}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày tạo</label>
                  <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                    {new Date(emailNotification.createdAt).toLocaleString('vi-VN')}
                  </p>
                </div>
                {emailNotification.sentAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ngày gửi</label>
                    <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                      {new Date(emailNotification.sentAt).toLocaleString('vi-VN')}
                    </p>
                  </div>
                )}
                {emailNotification.failureReason && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lý do thất bại</label>
                    <p className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                      {emailNotification.failureReason}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Email Content */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-800">Nội dung thông báo</h3>
              </div>
              <iframe
                title="Nội dung thông báo phí"
                srcDoc={emailNotification.content}
                className="w-full border-0"
                style={{ minHeight: '70vh' }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailModal; 